import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  CalendarBlank,
  CheckCircle,
  CircleNotch,
  CurrencyInr,
  MagnifyingGlass,
  MapPin,
  PaperPlaneTilt,
  Users,
  X,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const careersBaseUrl =
  process.env.REACT_APP_CAREERS_API_URL || process.env.REACT_APP_BACKEND_URL || "";
const CAREERS_API = `${careersBaseUrl}/api/careers`;

const emptyApplication = {
  fullName: "",
  email: "",
  phone: "",
  experience: "",
  location: "",
  currentCompany: "",
  skills: [],
  resumeUrl: "",
  resumeFileName: "",
  coverLetter: "",
};

const skillOptions = Array.from(new Set([
  "Communication (Written & Verbal)",
  "Problem Solving",
  "Time Management",
  "Team Collaboration",
  "Adaptability & Flexibility",
  "Customer Service Orientation",
  "Data Entry & Typing Speed",
  "Microsoft Office (Word, Excel, PowerPoint)",
  "Google Workspace (Docs, Sheets, Slides)",
  "Project Management Tools (Trello, Asana, Jira)",
  "Slack / Team Communication Tools",
  "Basic Math & Analytical Thinking",
  "Full-Stack Development",
  "Frontend Development",
  "Backend Development",
  "Mobile App Development (iOS/Android)",
  "UI/UX Design",
  "Cloud Infrastructure (AWS/GCP/Azure)",
  "Database Management (SQL/NoSQL)",
  "E-commerce Platform Customization",
  "Payment Gateway Integration",
  "API Development & Integration",
  "QA Testing & Automation",
  "Search Engine Optimization (SEO)",
  "Pay-Per-Click Advertising (PPC)",
  "Social Media Marketing (SMM)",
  "Performance Marketing (Meta/Google Ads)",
  "Email & SMS Automation",
  "Conversion Rate Optimization (CRO)",
  "Content Writing & Copywriting",
  "Influencer Marketing",
  "Google Analytics & Data Tracking",
  "Brand Strategy",
  "Inventory Management",
  "Supply Chain Forecasting",
  "Vendor Management & Sourcing",
  "Warehouse Operations",
  "3PL & Fleet Management",
  "Order Fulfillment & Packing",
  "Reverse Logistics & Returns Management",
  "Procurement & Purchasing",
  "Helpdesk Software",
  "Live Chat Support",
  "Email Support",
  "Phone Support",
  "Escalation Management",
  "Customer Relationship Management (CRM)",
  "Order Tracking & Resolution",
  "Catalog Management",
  "Product Data Entry",
  "Digital Visual Merchandising",
  "Product Photography & Editing",
  "Pricing & Promotion Strategy",
  "Competitor Pricing Analysis",
  "E-commerce Accounting & COGS Tracking",
  "Tax Compliance & Filing",
  "Fraud Detection & Chargeback Management",
  "Contract Negotiation",
  "Talent Acquisition & Recruiting",
  "Payroll Management",
  "React",
  "JavaScript",
  "TypeScript",
  "Node.js",
  "Python",
  "FastAPI",
  "MongoDB",
  "SQL",
  "REST APIs",
  "UI/UX",
  "Tailwind CSS",
  "Operations",
  "Logistics",
  "Fleet Management",
  "Customer Support",
  "Sales",
  "Business Development",
  "Lead Generation",
  "CRM",
  "Vendor Management",
  "Supplier Onboarding",
  "Construction Materials",
  "Field Sales",
  "Tamil Communication",
  "English Communication",
  "Hindi Communication",
  "Excel",
  "Google Sheets",
  "Data Analysis",
  "Hiring analytics",
  "Communication",
  "Team Coordination",
]));

const formatDeadline = (deadline) => {
  if (!deadline) return "Open until filled";
  const date = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(date.getTime())) return deadline;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const splitText = (value) => {
  if (!value) return [];
  return value
    .split(/\n|\. /)
    .map((item) => item.replace(/\.$/, "").trim())
    .filter(Boolean);
};

export default function CareerPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [jobsError, setJobsError] = useState("");
  const [formData, setFormData] = useState(emptyApplication);
  const [skillSearch, setSkillSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      setIsLoadingJobs(true);
      setJobsError("");
      try {
        const response = await axios.get(`${CAREERS_API}/jobs`);
        const openJobs = response.data?.jobs || [];
        setJobs(openJobs);
        setSelectedJobId(openJobs[0]?.id || "");
      } catch (error) {
        setJobsError("We could not load open roles right now. Please try again later.");
      } finally {
        setIsLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobId) || jobs[0],
    [jobs, selectedJobId]
  );

  const filteredSkills = useMemo(() => {
    const search = skillSearch.trim().toLowerCase();
    if (!search) return skillOptions;
    return skillOptions.filter((skill) => skill.toLowerCase().includes(search));
  }, [skillSearch]);

  const applicationDisabled =
    isSubmitting ||
    !selectedJob ||
    !formData.fullName ||
    !formData.email ||
    !formData.phone ||
    !formData.experience;

  const handleJobSelect = (jobId) => {
    setSelectedJobId(jobId);
    setIsSubmitted(false);
  };

  const handleResumeFile = (event) => {
    const file = event.target.files?.[0];
    setFormData({
      ...formData,
      resumeFileName: file?.name || "",
    });
  };

  const toggleSkill = (skill) => {
    const isSelected = formData.skills.includes(skill);
    setFormData({
      ...formData,
      skills: isSelected
        ? formData.skills.filter((item) => item !== skill)
        : [...formData.skills, skill],
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedJob) return;

    setIsSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([, value]) =>
          Array.isArray(value) ? value.length > 0 : value
        )
      );
      await axios.post(`${CAREERS_API}/jobs/${selectedJob.id}/apply`, payload);
      setIsSubmitted(true);
      setFormData(emptyApplication);
      setSkillSearch("");
      toast.success("Application submitted successfully.");
    } catch (error) {
      const fields = error.response?.data?.fields;
      const detail = error.response?.data?.detail;
      const message = fields?.length
        ? `Please complete: ${fields.join(", ")}`
        : typeof detail === "string"
          ? detail
        : "Failed to submit application. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={20} weight="bold" />
          <span className="font-medium">Back to Home</span>
        </button>

        <section className="grid lg:grid-cols-[1fr_0.8fr] gap-10 items-start mb-12">
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <Briefcase size={20} weight="duotone" className="text-[#0055FF]" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                Careers
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0F172A] mb-4">
              Build the operating layer for construction commerce
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
              Join Goodskart and Loado as we make construction material sourcing,
              fleet movement, and partner operations faster for teams across India.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0F172A] mb-4">Why Loado</h2>
            <div className="grid sm:grid-cols-3 lg:grid-cols-1 gap-3">
              {[
                "High-impact logistics and marketplace problems",
                "Small teams with direct ownership",
                "Chennai-first operations with India-scale ambition",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle size={20} weight="fill" className="text-green-500 mt-0.5" />
                  <p className="text-sm text-slate-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          <section>
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-2xl font-black text-[#0F172A]">Open Roles</h2>
              <span className="text-sm font-semibold text-slate-500">
                {jobs.length} vacancy{jobs.length === 1 ? "" : "ies"}
              </span>
            </div>

            {isLoadingJobs && (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                <CircleNotch size={28} className="animate-spin text-[#0055FF] mx-auto mb-3" />
                <p className="text-slate-600">Loading current vacancies...</p>
              </div>
            )}

            {!isLoadingJobs && jobsError && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <p className="text-slate-600">{jobsError}</p>
              </div>
            )}

            {!isLoadingJobs && !jobsError && jobs.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <p className="text-slate-600">
                  There are no public vacancies at the moment. Please check back soon.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {jobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() => handleJobSelect(job.id)}
                  className={`w-full text-left bg-white border rounded-lg p-5 transition-colors ${
                    selectedJob?.id === job.id
                      ? "border-[#0055FF] shadow-sm"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-[#0F172A] text-lg">{job.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{job.department}</p>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-[#0055FF] bg-[#0055FF]/10 px-3 py-1 rounded-full">
                      {job.openings || 1} opening{job.openings === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm text-slate-600">
                    <span className="flex items-center gap-2">
                      <MapPin size={16} weight="duotone" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-2">
                      <Briefcase size={16} weight="duotone" />
                      {job.type} · {job.level}
                    </span>
                    {job.salary && (
                      <span className="flex items-center gap-2">
                        <CurrencyInr size={16} weight="duotone" />
                        {job.salary}
                      </span>
                    )}
                    <span className="flex items-center gap-2">
                      <CalendarBlank size={16} weight="duotone" />
                      {formatDeadline(job.deadline)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-lg p-6 lg:p-8">
            {selectedJob ? (
              <>
                <div className="border-b border-slate-200 pb-6 mb-6">
                  <p className="text-sm font-semibold text-[#0055FF] mb-2">
                    {selectedJob.department}
                  </p>
                  <h2 className="text-2xl lg:text-3xl font-black text-[#0F172A] mb-3">
                    {selectedJob.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed">{selectedJob.summary}</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: MapPin, label: "Location", value: selectedJob.location },
                    { icon: Users, label: "Level", value: selectedJob.level },
                    { icon: Briefcase, label: "Employment", value: selectedJob.type },
                    { icon: CalendarBlank, label: "Apply By", value: formatDeadline(selectedJob.deadline) },
                  ].map((item) => (
                    <div key={item.label} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                        <item.icon size={16} weight="duotone" />
                        {item.label}
                      </div>
                      <p className="font-semibold text-[#0F172A]">{item.value || "Not specified"}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-6 mb-8">
                  {[
                    ["Responsibilities", selectedJob.responsibilities],
                    ["Requirements", selectedJob.requirements],
                    ["Preferred", selectedJob.preferred],
                  ].map(([title, value]) => {
                    const items = splitText(value);
                    if (!items.length) return null;
                    return (
                      <div key={title}>
                        <h3 className="font-bold text-[#0F172A] mb-3">{title}</h3>
                        <ul className="space-y-2">
                          {items.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                              <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-xl font-black text-[#0F172A] mb-4">Apply for this role</h3>

                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto mb-3" />
                      <h4 className="font-bold text-[#0F172A] mb-2">Application received</h4>
                      <p className="text-slate-600 mb-5">
                        Our hiring team will review your profile and contact you if there is a fit.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="text-[#0055FF] font-semibold hover:underline"
                      >
                        Submit another application
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="career_full_name">Full Name *</Label>
                          <Input
                            id="career_full_name"
                            value={formData.fullName}
                            onChange={(event) => setFormData({ ...formData, fullName: event.target.value })}
                            placeholder="Riya Sharma"
                            required
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="career_email">Email *</Label>
                          <Input
                            id="career_email"
                            type="email"
                            value={formData.email}
                            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                            placeholder="riya@example.com"
                            required
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="career_phone">Phone *</Label>
                          <Input
                            id="career_phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(event) => setFormData({ ...formData, phone: event.target.value })}
                            placeholder="+91 98765 00000"
                            required
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="career_experience">Experience *</Label>
                          <Input
                            id="career_experience"
                            value={formData.experience}
                            onChange={(event) => setFormData({ ...formData, experience: event.target.value })}
                            placeholder="5 years"
                            required
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="career_location">Current Location</Label>
                          <Input
                            id="career_location"
                            value={formData.location}
                            onChange={(event) => setFormData({ ...formData, location: event.target.value })}
                            placeholder="Bengaluru"
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="career_company">Current Company</Label>
                          <Input
                            id="career_company"
                            value={formData.currentCompany}
                            onChange={(event) => setFormData({ ...formData, currentCompany: event.target.value })}
                            placeholder="Example Co"
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="career_resume_url">Resume URL</Label>
                        <Input
                          id="career_resume_url"
                          type="url"
                          value={formData.resumeUrl}
                          onChange={(event) => setFormData({ ...formData, resumeUrl: event.target.value })}
                          placeholder="https://cdn.example.com/resume.pdf"
                          className="mt-1.5"
                        />
                      </div>

                      <div>
                        <Label htmlFor="career_skill_search">Skills</Label>
                        <div className="mt-1.5 border border-slate-200 rounded-lg p-3 bg-slate-50">
                          {formData.skills.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {formData.skills.map((skill) => (
                                <button
                                  key={skill}
                                  type="button"
                                  onClick={() => toggleSkill(skill)}
                                  className="inline-flex items-center gap-1 rounded-full bg-[#0055FF] px-3 py-1.5 text-xs font-semibold text-white"
                                >
                                  {skill}
                                  <X size={12} weight="bold" />
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="relative">
                            <MagnifyingGlass
                              size={18}
                              weight="duotone"
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <Input
                              id="career_skill_search"
                              value={skillSearch}
                              onChange={(event) => setSkillSearch(event.target.value)}
                              placeholder="Search skills"
                              className="pl-10 bg-white"
                            />
                          </div>
                          <div className="mt-3 max-h-44 overflow-y-auto pr-1">
                            <div className="flex flex-wrap gap-2">
                              {filteredSkills.map((skill) => {
                                const isSelected = formData.skills.includes(skill);
                                return (
                                  <button
                                    key={skill}
                                    type="button"
                                    onClick={() => toggleSkill(skill)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                      isSelected
                                        ? "border-[#0055FF] bg-[#0055FF]/10 text-[#0055FF]"
                                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                    }`}
                                  >
                                    {skill}
                                  </button>
                                );
                              })}
                              {filteredSkills.length === 0 && (
                                <p className="text-sm text-slate-500 py-2">No matching skills found.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="career_resume_file">Resume File Name</Label>
                        <Input
                          id="career_resume_file"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeFile}
                          className="mt-1.5"
                        />
                        {formData.resumeFileName && (
                          <p className="text-xs text-slate-500 mt-2">
                            Selected: {formData.resumeFileName}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="career_cover_letter">Cover Letter</Label>
                        <Textarea
                          id="career_cover_letter"
                          value={formData.coverLetter}
                          onChange={(event) => setFormData({ ...formData, coverLetter: event.target.value })}
                          placeholder="Tell us why this role interests you..."
                          rows={5}
                          className="mt-1.5 resize-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={applicationDisabled}
                        className="w-full py-3 bg-[#0055FF] text-white font-semibold hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 rounded-lg"
                      >
                        {isSubmitting ? (
                          <>
                            <CircleNotch size={20} className="animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <PaperPlaneTilt size={20} weight="bold" />
                            Submit Application
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <Briefcase size={48} weight="duotone" className="text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600">Select an open role to view details and apply.</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
